// Phase 10B "AutoMind 2050" — AI Agents.
//
// Premium enterprise styling: ink/gold palette, frosted DataCards, no neon.
// All Retell API calls preserved.

import React, { useState, useEffect } from 'react';
import { Phone, ExternalLink, RefreshCw } from 'lucide-react';

import { PageHeader } from '../components/ui/page-header';
import { TabBar } from '../components/ui/tab-bar';
import { DataCard } from '../components/ui/data-card';
import { SectionGrid } from '../components/ui/section-grid';
import { EmptyState } from '../components/ui/empty-state';
import { StatusPill } from '../components/ui/status-pill';
import { toast } from '../hooks/use-toast';
import { retellApi } from '../api';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errored, setErrored] = useState(false);
  const [tab, setTab] = useState('agents');

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrored(false);
      // Phase Audit Fix C follow-up: previously this used bare fetch()
      // without an auth header, so /api/retell/agents always 401'd and
      // the page silently rendered "No agents found" regardless of the
      // backend's RETELL_API_KEY state. Route through retellApi (which
      // wraps apiFetch + JWT) so the request is actually authenticated.
      const [agentsRes, phonesRes] = await Promise.all([
        retellApi.agents(),
        retellApi.phoneNumbers(),
      ]);

      if (agentsRes?.success) {
        setAgents(agentsRes.agents || []);
      }

      if (phonesRes?.success) {
        setPhoneNumbers(phonesRes.phone_numbers || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrored(true);
      toast({
        title: 'Error',
        description: error?.payload?.detail || error?.message || 'Failed to load agents and phone numbers',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const getPhoneNumberForAgent = (agentId) => {
    return phoneNumbers.find(p => p.assigned_agent?.agent_id === agentId);
  };

  const tabs = [
    { id: 'agents', label: 'Agents', badge: agents.length || undefined },
    { id: 'phones', label: 'Phone numbers', badge: phoneNumbers.length || undefined },
    { id: 'templates', label: 'Templates' },
  ];

  return (
    <div className="space-y-6" data-testid="agents-page">
      <PageHeader
        eyebrow="AI"
        title="AI Agents"
        description="Voice agents and phone numbers."
        actions={
          <>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-2050 btn-secondary"
              data-testid="refresh-agents-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => window.open('https://dashboard.retellai.com', '_blank')}
              className="btn-2050 btn-primary"
              data-testid="create-agent-btn"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open Retell
            </button>
          </>
        }
      />

      <TabBar value={tab} onChange={setTab} items={tabs} />

      {errored ? (
        <DataCard>
          <EmptyState
            title="Unable to load right now."
            description="Please try refreshing in a moment."
          />
        </DataCard>
      ) : tab === 'agents' ? (
        loading ? (
          <SectionGrid cols={3}>
            {[1, 2, 3].map(i => (
              <DataCard key={i} loading />
            ))}
          </SectionGrid>
        ) : agents.length === 0 ? (
          <DataCard>
            <EmptyState
              title="No agents found"
              description="Create your first AI agent in Retell to get started."
            >
              <button
                type="button"
                onClick={() => window.open('https://dashboard.retellai.com', '_blank')}
                className="btn-2050 btn-primary"
                data-testid="create-first-agent-btn"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Go to Retell Dashboard
              </button>
            </EmptyState>
          </DataCard>
        ) : (
          <SectionGrid cols={3} data-testid="agents-grid">
            {agents.map(agent => {
              const phone = getPhoneNumberForAgent(agent.agent_id);
              return (
                <DataCard
                  key={agent.agent_id}
                  data-testid={`agent-card-${agent.agent_id}`}
                  title={agent.name || 'Unnamed Agent'}
                  description={agent.agent_id}
                  actions={
                    <StatusPill
                      tone={agent.archived ? 'neutral' : 'success'}
                      label={agent.archived ? 'Archived' : 'Active'}
                      dot
                    />
                  }
                >
                  <div className="space-y-4">
                    {phone ? (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-3.5 w-3.5 text-ink-500 flex-shrink-0" />
                        <span className="font-mono nums">{phone.phone_number}</span>
                        <StatusPill tone="neutral" label={phone.label} />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-ink-500">
                        <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>No phone number assigned</span>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-ink-200/60 dark:border-ink-800/60">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">Created</p>
                        <p className="text-sm font-medium nums">
                          {agent.created_at_unix_secs
                            ? new Date(agent.created_at_unix_secs * 1000).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.16em] text-ink-500 mb-1">Provider</p>
                        <p className="text-sm font-medium capitalize">{phone?.provider || '—'}</p>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        className="btn-2050 btn-secondary flex-1"
                        onClick={() => {
                          navigator.clipboard.writeText(agent.agent_id);
                          toast({ title: 'Copied!', description: 'Agent ID copied to clipboard' });
                        }}
                        data-testid={`copy-agent-id-${agent.agent_id}`}
                      >
                        Copy ID
                      </button>
                      <button
                        type="button"
                        className="btn-2050 btn-ghost flex-1"
                        onClick={() => window.open('https://dashboard.retellai.com', '_blank')}
                        data-testid={`edit-agent-${agent.agent_id}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        Edit
                      </button>
                    </div>
                  </div>
                </DataCard>
              );
            })}
          </SectionGrid>
        )
      ) : tab === 'phones' ? (
        phoneNumbers.length === 0 ? (
          <DataCard>
            <EmptyState
              title="No phone numbers"
              description="Connect a phone number in Retell to start placing outbound calls."
            />
          </DataCard>
        ) : (
          <SectionGrid cols={3}>
            {phoneNumbers.map(phone => (
              <DataCard
                key={phone.phone_number_id}
                title={phone.phone_number}
                description={phone.label}
                actions={
                  <StatusPill tone="neutral" label={phone.provider} />
                }
              >
                <div className="space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    <StatusPill
                      tone={phone.supports_inbound ? 'success' : 'neutral'}
                      label={`${phone.supports_inbound ? 'Yes' : 'No'} · Inbound`}
                    />
                    <StatusPill
                      tone={phone.supports_outbound ? 'success' : 'neutral'}
                      label={`${phone.supports_outbound ? 'Yes' : 'No'} · Outbound`}
                    />
                  </div>
                  {phone.assigned_agent && (
                    <div className="pt-3 border-t border-ink-200/60 dark:border-ink-800/60 text-xs text-ink-500">
                      Assigned to:{' '}
                      <span className="font-medium text-ink-900 dark:text-ink-50">
                        {phone.assigned_agent.agent_name}
                      </span>
                    </div>
                  )}
                </div>
              </DataCard>
            ))}
          </SectionGrid>
        )
      ) : (
        <DataCard>
          <EmptyState
            title="No templates yet"
            description="Save and reuse common agent configurations as templates."
          />
        </DataCard>
      )}
    </div>
  );
};

export default Agents;
